import { getCurrentUserSessionAccessToken } from '@/storage'
import { loggedFetch } from '@/utils/api-logger'
import { redirectToErrorPage } from '@/utils/route-utils'

/**
 * Fetches data from the server with authorization.
 * @param {Object} options - The options for the fetch request.
 * @param {string} options.path - The path of the endpoint to fetch from.
 * @param {string} [options.method='GET'] - The HTTP method for the request.
 * @param {Object} [options.body] - The request body data (if applicable).
 * @returns {Promise<Object>} A promise that resolves to the parsed JSON response.
 * @throws {Error} Throws an error if the response status is not ok.
 */
export default async function fetchWithAuthorization({ path, method = 'GET', body }) {
  try {
    const accessToken = getCurrentUserSessionAccessToken()
    const response = await loggedFetch(import.meta.env.VITE_SERVER_DOMAIN + path, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        'x-app-name': 'FASTEN_WEB',
        'x-app-version': import.meta.env.VITE_APP_VERSION,
      },
      body,
    })

    if (response.status === 401) {
      console.error('Unauthorized')
      // TODO: if response fails with 401 (Unauthorized), refresh tokens
    } else if (!response.ok) {
      throw response
    }

    if (response.headers.get('Content-Type') === 'application/pdf') {
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      return url
    }

    // Handle cases where the response might be empty
    const text = await response.text()
    return text ? JSON.parse(text) : {}
  } catch (error) {
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      // 🚨 CORS / WAF / Server down / Network failure
      console.log('>>> Network error', error)
      redirectToErrorPage('network', error.status ?? 500)
    } else if (error.status >= 500) {
      // ⚙️ Unexpected server error (logic bug, DB bug, 3rd-party service bug, etc)
      console.log('>>> Unexpected server error', error)
      redirectToErrorPage('server', error.status)
    } else {
      // 🚦 Backend-defined error
      const errorBody = await error.json()
      console.log('>>> API error', errorBody)
      // Re-throw the error so React Query or other callers can handle it
      throw errorBody
    }
  }
}
